import { QuizImportMethods } from '@showbie-socrative/socrative-utils';

import { joinSocrativeURL, teacherServiceURL } from '../endpoint';
import { WorkspaceRole } from './getWorkspaceMembers';

export const workspacesURL = () => {
  return new URL(`workspaces`, teacherServiceURL);
};

export const inviteToWorkspaceURL = (code: string) => {
  const url = new URL('w', joinSocrativeURL);
  url.pathname += `/${code}`;
  return url;
};

export const workspaceIdURL = (workspaceId: string) => {
  const url = workspacesURL();
  url.pathname += `/${workspaceId}`;
  return url;
};

export const workspaceSettingsURL = (workspaceId: string) => {
  const url = workspaceIdURL(workspaceId);
  url.pathname += `/settings`;
  return url;
};

export const removeWorkspaceMemberURL = (
  workspaceId: string,
  userId: string
) => {
  const url = workspacesURL();
  url.pathname += `/${workspaceId}/users/${userId}`;
  return url;
};

export const workspaceOwnershipURL = (workspaceId: string) => {
  const url = workspacesURL();
  url.pathname += `/${workspaceId}/owner`;
  return url;
};

export const workspaceByCodeURL = (workspaceCode: string) => {
  const url = workspacesURL();
  url.pathname += `/code/${workspaceCode}`;
  return url;
};

export const workspaceRequestingUserRoleURL = (workspaceId: string) => {
  const url = workspaceIdURL(workspaceId);
  url.pathname += `/members/requesting_user`;
  return url;
};

export const changeWorkspaceRoleURL = (workspaceId: string, userId: string) => {
  const url = workspaceIdURL(workspaceId);
  url.pathname += `/users/${userId}/role`;
  return url;
};

export const joinWorkspaceURL = () => {
  const url = workspacesURL();
  url.pathname += `/join`;
  return url;
};

export const workspaceMembersURL = (workspaceId: string) => {
  const url = workspaceIdURL(workspaceId);
  url.pathname += '/members';
  return url;
};

export const workspaceQuizzesURL = ({
  workspaceId,
  isDeleted,
}: {
  workspaceId: string;
  isDeleted?: boolean;
}) => {
  const url = workspaceIdURL(workspaceId);
  url.pathname += `/quizzes`;
  if (isDeleted) {
    url.searchParams.append('deleted', `${isDeleted}`);
  }
  return url;
};

interface WorkspaceFoldersURLParams {
  workspaceId: string;
  isDeleted?: boolean;
}

export const workspaceFoldersURL = ({
  workspaceId,
  isDeleted,
}: WorkspaceFoldersURLParams) => {
  const url = workspaceIdURL(workspaceId);
  url.pathname += `/folders`;
  if (isDeleted) {
    url.searchParams.append('deleted', `${isDeleted}`);
  }
  return url;
};

export const workspaceCodeURL = ({
  workspaceId,
  workspaceRole,
}: {
  workspaceId: string;
  workspaceRole: WorkspaceRole;
}) => {
  const url = workspaceIdURL(workspaceId);
  url.pathname += `/code`;
  url.searchParams.append('role', `${workspaceRole}`);
  return url;
};

export const workspaceCodeStatusesURL = (workspaceId: string) => {
  const url = workspaceIdURL(workspaceId);
  url.pathname += `/code/statuses`;
  return url;
};

export const copyToPersonalURL = () => {
  const url = workspacesURL();
  url.pathname += '/quizzes/personal';
  return url;
};

export const copyWorkspaceQuizURL = (workspaceId: string) => {
  const url = workspacesURL();
  url.pathname += `/${workspaceId}/quizzes/copy`;
  return url;
};

export function moveWorkspaceQuizzesURL(workspaceId: string) {
  const url = workspacesURL();
  url.pathname += `/${workspaceId}/quizzes/move`;
  return url;
}

export function importWorkspaceQuizzesBySocURL({
  workspaceId,
  method,
}: {
  workspaceId: string;
  method: QuizImportMethods;
}) {
  const url = workspacesURL();
  url.pathname += `/${workspaceId}/quizzes/import`;
  url.searchParams.append('method', method);
  return url;
}

export function createWorkspaceQuizUrl(workspaceId: string) {
  const url = workspaceIdURL(workspaceId);
  url.pathname += '/quiz';
  return url;
}

export const workspaceFolderURL = ({
  workspaceId,
  workspaceFolderId,
}: {
  workspaceId: string;
  workspaceFolderId: string;
}) => {
  const url = workspaceFoldersURL({ workspaceId: workspaceId });
  url.pathname += `/${workspaceFolderId}`;
  return url;
};

export function moveWorkspaceFoldersURL(workspaceId: string) {
  const url = workspaceFoldersURL({ workspaceId });
  url.pathname += `/move`;
  return url;
}

export function restoreWorkspaceQuizzesURL(workspaceId: string) {
  const url = workspaceQuizzesURL({ workspaceId });
  url.pathname += `/restore`;
  return url;
}

export function restoreWorkspaceFoldersURL(workspaceId: string) {
  const url = workspaceFoldersURL({ workspaceId });
  url.pathname += `/restore`;
  return url;
}

export const workspaceQuizURL = ({
  workspaceId,
  workspaceQuizId,
}: {
  workspaceId: string;
  workspaceQuizId: string;
}) => {
  const url = workspaceIdURL(workspaceId);
  url.pathname += `/quizzes/${workspaceQuizId}`;
  return url;
};

export function purgeWorkspaceQuizzesURL(workspaceId: string) {
  const url = workspaceQuizzesURL({ workspaceId });
  url.pathname += `/purge`;
  return url;
}

export function purgeWorkspaceFoldersURL(workspaceId: string) {
  const url = workspaceFoldersURL({ workspaceId });
  url.pathname += `/purge`;
  return url;
}

export const workspaceQuizLockURL = ({
  workspaceId,
  workspaceQuizId,
}: {
  workspaceId: string;
  workspaceQuizId: string;
}) => {
  const url = workspaceQuizURL({
    workspaceId,
    workspaceQuizId,
  });
  url.pathname += `/lock`;
  return url;
};

export function mergeWorkspaceQuizURL(workspaceId: string) {
  const url = workspaceQuizzesURL({ workspaceId });
  url.pathname += `/merge`;
  return url;
}

export const copyToWorkspaceURL = (workspaceId: string) => {
  const url = workspaceIdURL(workspaceId);
  url.pathname += `/quizzes/copy_to_workspace`;
  return url;
};
